// Global Instructions Rule Applied!
//
// What the compose form shows for "Send type" and when a mailbox campaign
// may be queued (capital call notices, stage 6a). Pure, so the rules are
// testable without the form.
//
// The options, their labels and descriptions come from GET /email/channels
// (cfg.app_config email/channels) - none of that copy lives here.

import type { EmailChannels } from '../../../state_manager/data_models/email_channels_model.ts'

export const CHANNEL_MAILBOX = 'mailbox'

export interface ChannelRadioOption {
  value: string
  label: string
  disabled: boolean
  /** The channel's description, or why it cannot be picked. */
  tooltip: string
}

export interface ChannelRadio {
  options: ChannelRadioOption[]
  /** The whole control is read-only: the options could not be loaded, or the
   *  campaign's send type is one this environment does not list. Its value is
   *  kept as loaded either way - never swapped for a listed one. */
  disabled: boolean
}

export const UNLISTED_CHANNEL_TOOLTIP =
  'This send type is not configured in this environment, so it cannot be changed here.'

/**
 * The Send type radio for a campaign whose channel is `currentCode`.
 *
 * Every cfg channel is an option; one the environment cannot send on is
 * disabled with its reason. A campaign whose channel is not among them (an
 * unknown code, or the options failed to load) shows that code as the only
 * selected, disabled choice.
 */
export function buildChannelRadio(channels: EmailChannels | null, currentCode: string): ChannelRadio {
  const listed = channels?.channels ?? []
  const options: ChannelRadioOption[] = listed.map((c) => ({
    value: c.code,
    label: c.label,
    disabled: !c.enabled,
    tooltip: c.enabled ? c.description : c.disabled_reason || c.description
  }))
  const isListed = listed.some((c) => c.code === currentCode)
  if (!isListed && currentCode) {
    options.push({ value: currentCode, label: currentCode, disabled: true, tooltip: UNLISTED_CHANNEL_TOOLTIP })
  }
  return { options, disabled: !isListed }
}

/**
 * Why a campaign on `channel` sent as `senderEmail` cannot be queued, or null
 * when the channel does not stand in the way. Only the mailbox channel can:
 * it must be switched on in this environment and the sender's mailbox must be
 * enabled for campaign sends - the two refusals queue_email_campaign raises,
 * said before the click.
 *
 * null while the channels are still unknown (not loaded): the queue gate
 * itself is the authority, the form only warns early.
 */
export function mailboxQueueBlock(
  channels: EmailChannels | null,
  channel: string | undefined,
  senderEmail: string | null | undefined
): string | null {
  if (channel !== CHANNEL_MAILBOX || !channels) return null
  if (!channels.mailbox.enabled) {
    const option = channels.channels.find((c) => c.code === CHANNEL_MAILBOX)
    return option?.disabled_reason || 'Sending from a mailbox is switched off in this environment.'
  }
  const email = (senderEmail || '').trim().toLowerCase()
  if (!email) return null
  if (!channels.mailbox.sender_emails.includes(email)) {
    return `${email} is not enabled for sending from its mailbox. An administrator can switch it on under Configuration → Interaction Tracking (Campaign send).`
  }
  return null
}
